import { NormalizedLandmark } from '@mediapipe/tasks-vision';
import EventEmitter from '@services/EventEmitter';
import {
	assessPosture,
	determineOrientation,
	drawCenteringBox,
	drawCenterLine,
	drawExclusionOverlay,
	drawVerticalLine,
	FRONT_VIEW_CONNECTIONS,
	FullAssessmentValue,
	getAdaptiveThreshold,
	getOrientationConfidence,
	getSmoothedVisibility,
	isPersonInBoxFn,
	LEFT_VIEW_CONNECTIONS,
	RIGHT_VIEW_CONNECTIONS,
	updateVisibilityHistory,
} from '@utils/PoseDetectionUtility';
import { PersonOrientation } from '@utils/SkeletonMedia';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { videoSize } from '../constants';
import { PosturalAnalytics, UseControls } from '../context/Controls.context';
import { UseFullScreen } from '../context/FullScreen.context';
import { TransitionStates, UseTransition } from '../context/Transition.context';

function Lines() {
	const canvasLinesRef = useRef<HTMLCanvasElement>(null);
	const {
		onGetCurrent,
		isPersonInBox,
		setIsPersonInBox,
		setIsSkeletonVisible,
		setIsAligned,
		canvasDimensions,
	} = UseControls();

	const { transition } = UseTransition();
	const { isFullScreen } = UseFullScreen();
	const [timerTick, setTimerTick] = useState<number>(0);
	const [isLineAligned, setIsLineAligned] = useState(false);
	const [isFullVisible, setIsFullVisible] = useState(false);
	const [orientationConfidence, setOrientationConfidence] = useState<
		number | null
	>(null);
	const [orientation, setOrientation] = useState<PersonOrientation>(
		PersonOrientation.FRONT,
	);
	const [personOrientation, setPersonOrientation] = useState<PersonOrientation>(
		PersonOrientation.FRONT,
	);
	const [assessmentValue, setAssessmentValue] = useState<FullAssessmentValue>(
		{},
	);

	const latestLandmarksRef = useRef<NormalizedLandmark[] | null>(null);
	const visibilityHistory = useRef<Map<number, number[]>>(new Map());

	const [boxWidth, setBoxWidth] = useState<number>(
		canvasDimensions.width * 0.4,
	);
	const [boxHeight, setBoxHeight] = useState<number>(
		canvasDimensions.height * 0.9,
	);
	const [boxX, setBoxX] = useState<number>(
		(canvasDimensions.width - canvasDimensions.width * 0.4) / 2,
	);
	const [boxY, setBoxY] = useState<number>(
		(canvasDimensions.height - canvasDimensions.height * 0.9) / 2,
	);
	const [canvasHeight, setCanvasHeight] = useState<number>(
		canvasDimensions.height,
	);
	const [canvasWidth, setCanvasWidth] = useState<number>(
		canvasDimensions.width,
	);

	const current = onGetCurrent() as Partial<PosturalAnalytics>;

	useEffect(() => {
		if (canvasDimensions) {
			const newBoxWidth = canvasDimensions.width * 0.4;
			const newBoxHeight = canvasDimensions.height * 0.9;
			const newBoxX = (canvasDimensions.width - newBoxWidth) / 2;
			const newBoxY = (canvasDimensions.height - newBoxHeight) / 2;
			setCanvasWidth(canvasDimensions.width);
			setCanvasHeight(canvasDimensions.height);

			setBoxWidth(newBoxWidth);
			setBoxHeight(newBoxHeight);
			setBoxX(newBoxX);
			setBoxY(newBoxY);
		}
	}, [canvasDimensions.width, canvasDimensions.height]);

	const alignmentThreshold = 20;

	const frameCallback = useCallback(() => {
		if (!latestLandmarksRef.current) {
			return;
		}
		const results = latestLandmarksRef.current;
		const canvasLinesCtx = canvasLinesRef.current?.getContext(
			'2d',
		) as CanvasRenderingContext2D;

		const width: number = canvasWidth;
		const height: number = canvasHeight;

		canvasLinesCtx.save();
		canvasLinesCtx.clearRect(0, 0, width, height);

		const centerX = width / 2;
		if (transition?.value !== TransitionStates.READYSETGO) {
			// drawCenterLine(canvasLinesCtx, '#00AAFF', 15);
			drawCenteringBox(canvasLinesCtx, boxX, boxY, boxWidth, boxHeight);
		}

		updateVisibilityHistory(visibilityHistory.current, results);

		const connections = (() => {
			switch (current?.view) {
				case PersonOrientation.LEFT:
					return LEFT_VIEW_CONNECTIONS;
				case PersonOrientation.RIGHT:
					return RIGHT_VIEW_CONNECTIONS;
				default:
					return FRONT_VIEW_CONNECTIONS;
			}
		})();

		const requiredIndices = new Set<number>();
		connections.forEach(({ start, end }) => {
			requiredIndices.add(start);
			requiredIndices.add(end);
		});
		const orientationConfidence = getOrientationConfidence(
			visibilityHistory.current,
			results,
		);

		const allVisible = Array.from(requiredIndices).every(
			index =>
				getSmoothedVisibility(visibilityHistory.current, index) >
				getAdaptiveThreshold(index),
		);
		setIsFullVisible(allVisible);

		setOrientationConfidence(orientationConfidence);

		const personInBox = isPersonInBoxFn(
			results,
			boxX,
			boxY,
			boxWidth,
			boxHeight,
			width,
			height,
		);
		const facingDirection = determineOrientation(results);
		setPersonOrientation(facingDirection);
		setOrientation(facingDirection);
		let correctDirection = current?.view == facingDirection;
		if (
			current?.view === PersonOrientation.BACK &&
			facingDirection === PersonOrientation.FRONT
		) {
			// If the person is facing the camera, we consider it aligned
			correctDirection = true;
		}
		setIsLineAligned(allVisible && correctDirection);

		if (!personInBox) {
			drawExclusionOverlay(canvasLinesCtx, boxX, boxY, boxWidth, boxHeight);
		}
		setIsPersonInBox(personInBox && allVisible);
		const postureAssessments = assessPosture(
			results,
			width,
			height,
			canvasLinesCtx,
			current?.view,
		);

		if (transition?.value === TransitionStates.READYSETGO) {
			setAssessmentValue({
				...assessmentValue,
				[current?.view as string]: postureAssessments,
			});
		}

		// if (current?.view === 'left') {
		// 	const ankleLeft = results[27].x * width;
		// 	if (transition?.value !== TransitionStates.READYSETGO) {
		// 		drawVerticalLine(canvasLinesCtx, 'red', ankleLeft);
		// 	}
		// 	const ankleAligned = Math.abs(ankleLeft - centerX) <= alignmentThreshold;
		// 	setIsLineAligned(ankleAligned);
		// } else if (current?.view === 'right') {
		// 	const angleRight = results[28].x * width;
		// 	if (transition?.value !== TransitionStates.READYSETGO) {
		// 		drawVerticalLine(canvasLinesCtx, 'red', angleRight);
		// 	}
		// 	const ankleAligned = Math.abs(angleRight - centerX) <= alignmentThreshold;
		// 	setIsLineAligned(ankleAligned);
		// } else {
		// 	const noseX = results[0].x * width;
		// 	if (transition?.value !== TransitionStates.READYSETGO) {
		// 		drawVerticalLine(canvasLinesCtx, 'red', noseX);
		// 	}

		// 	const shoulderMidpointX = ((results[12].x + results[11].x) * width) / 2;
		// 	// drawVerticalLine(canvasLinesCtx, 'green', shoulderMidpointX); for testing

		// 	const hipMidpointX = ((results[23].x + results[24].x) * width) / 2;
		// 	// drawVerticalLine(canvasLinesCtx, 'yellow', hipMidpointX); for testing

		// 	const noseAligned = Math.abs(noseX - centerX) <= alignmentThreshold;
		// 	const shouldersAligned =
		// 		Math.abs(shoulderMidpointX - centerX) <= alignmentThreshold;
		// 	const hipsAligned =
		// 		Math.abs(hipMidpointX - centerX) <= alignmentThreshold;

		// 	const currentlyAligned = noseAligned && shouldersAligned && hipsAligned;

		// 	setIsLineAligned(currentlyAligned);

		// }
		if (
			[
				PersonOrientation.FRONT,
				PersonOrientation.BACK,
				PersonOrientation.RIGHT,
				PersonOrientation.LEFT,
			].includes(facingDirection)
		) {
			setIsSkeletonVisible(personInBox);
		}

		canvasLinesCtx.restore();
	}, [
		canvasWidth,
		canvasHeight,
		transition?.value,
		boxX,
		boxY,
		boxWidth,
		boxHeight,
		current?.view,
		setIsPersonInBox,
		assessmentValue,
		setIsSkeletonVisible,
	]);

	useEffect(() => {
		EventEmitter.on('results', results => {
			latestLandmarksRef.current = results;
			frameCallback();
		});

		return () => {
			EventEmitter.removeListener('results');
		};
	}, [current, frameCallback, transition, timerTick]);

	useEffect(() => {
		if (isLineAligned && isPersonInBox) {
			setIsAligned(true);
		} else {
			setIsAligned(false);
		}
	}, [isLineAligned]);

	useEffect(() => {
		const timerId = setTimeout(() => {
			setTimerTick(prev => prev + 1);
			frameCallback();
		}, 1000);

		return () => {
			clearTimeout(timerId);
		};
	}, [timerTick, frameCallback]);

	return (
		<>
			<canvas
				id="canvasLines"
				ref={canvasLinesRef}
				width={canvasDimensions.width}
				height={canvasDimensions.height}
				style={{
					maxWidth: isFullScreen ? '100%' : 1280,
					height: isFullScreen ? '100%' : 720,
					aspectRatio: '16/9',
					width: '100%',
					zIndex: 5,
					position: 'absolute',
					pointerEvents: 'none',
					top: 0,
					left: 0,
				}}
			/>

			{/* <div
				style={{
					position: 'absolute',
					bottom: 10,
					right: '50%',
					background: '#6b2ba0',
					color: 'white',
					padding: '10px',
					borderRadius: '5px',
					zIndex: 20,
					display: 'flex',
					flexDirection: 'column',
					gap: '5px',
					fontSize: '35px',
				}}>
				{orientationConfidence !== null && (
					<p>Orientation Confidence: {orientationConfidence.toFixed(2)}</p>
				)}
				{personOrientation && <p>Person Orientation: {personOrientation}</p>}
				<p> Full Visibility: {isFullVisible ? 'Yes' : 'No'}</p>
			</div> */}

			{/* {current?.view !== personOrientation && current?.view !== undefined && (
				<div
					style={{
						position: 'absolute',
						bottom: 10,
						left: '50%',
						background: '#6b2ba0',
						color: 'white',
						padding: '10px',
						borderRadius: '5px',
						zIndex: 20,
						display: 'flex',
						flexDirection: 'column',
						gap: '5px',
						maxWidth: 370,
					}}>
					<p
						style={{
							fontSize: '32px',
							fontWeight: 'bold',
							margin: '0 0 5px 0',
						}}>
						{current?.view !== personOrientation && `Turn ${current?.view} `}
					</p>
				</div>
			)} */}
		</>
	);
}

export default memo(Lines);
